/**
 * CreatePromptModal
 *
 * Modal for creating a brand-new prompt key with initial template content.
 * Includes a taxonomy selector to link the prompt to a taxonomy.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../ui/dialog';
import { Button } from '../../../../ui/button';
import { Textarea } from '../../../../ui/textarea';
import { Label } from '../../../../ui/label';
import { Input } from '../../../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { useCreatePromptDraft, usePromptKeys } from '../../hooks/usePrompts';
import { useTaxonomyKeys, useVersionList, useVersionDetail } from '../../hooks/useTaxonomy';

interface CreatePromptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Default taxonomy to pre-select */
  taxonomyKey: string;
  /** If provided, creates for an existing key (no key input shown) */
  existingPromptKey?: string;
  existingDisplayName?: string;
}

const CreatePromptModal: React.FC<CreatePromptModalProps> = ({
  open,
  onOpenChange,
  taxonomyKey: defaultTaxonomyKey,
  existingPromptKey,
  existingDisplayName,
}) => {
  const [promptKey, setPromptKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [selectedTaxonomyKey, setSelectedTaxonomyKey] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const createDraft = useCreatePromptDraft();
  const { data: taxonomyKeys } = useTaxonomyKeys();

  const isExisting = !!existingPromptKey;
  const effectiveKey = existingPromptKey || promptKey;
  const effectiveDisplayName = existingDisplayName || displayName;

  // Fetch the most relevant taxonomy version's dimensions for validation.
  // Priority: newest draft → live → skip archived. Matches the backend's
  // get_latest() semantics so the UI and API agree on which dimensions
  // are valid for prompt authoring.
  const { data: versions } = useVersionList(selectedTaxonomyKey);
  const relevantVersion = versions?.find((v) => v.status === 'draft')
    ?? versions?.find((v) => v.status === 'live')
    ?? null;
  const relevantVersionId = relevantVersion?.id ?? '';
  const { data: latestVersion } = useVersionDetail(selectedTaxonomyKey, relevantVersionId);
  const dimensionIds = latestVersion?.taxonomyData?.dimensions?.map((d) => d.id) ?? [];
  // Check if a prompt already exists for this key in the selected taxonomy.
  // Only relevant for new keys (not when adding a version to an existing key).
  const { data: existingPrompts } = usePromptKeys(selectedTaxonomyKey || null);
  const promptAlreadyExists =
    !isExisting &&
    !!effectiveKey &&
    !!selectedTaxonomyKey &&
    !!existingPrompts?.some((p) => p.promptKey === effectiveKey);

  const isNonDimension = effectiveKey === 'system';
  const dimensionValid = isNonDimension || !effectiveKey || dimensionIds.length === 0 || dimensionIds.includes(effectiveKey);

  const handleSubmit = () => {
    if (!effectiveKey.trim() || !templateContent.trim() || !selectedTaxonomyKey || !dimensionValid || promptAlreadyExists) return;

    createDraft.mutate(
      {
        promptKey: effectiveKey,
        options: {
          createNew: true,
          templateContent,
          taxonomyKey: selectedTaxonomyKey,
          // Dimension prompts are one-to-one with their prompt_key by backend
          // rule — non-dimension prompts (e.g. "system") omit the dimension.
          ...(effectiveKey !== 'system' ? { dimension: effectiveKey } : {}),
          ...(effectiveDisplayName ? { displayName: effectiveDisplayName } : {}),
        },
      },
      {
        onSuccess: () => {
          resetForm();
          onOpenChange(false);
        },
      }
    );
  };

  const resetForm = () => {
    setPromptKey('');
    setDisplayName('');
    setTemplateContent('');
    setSelectedTaxonomyKey('');
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isExisting ? `Create First Version for ${existingDisplayName}` : 'Create New Prompt'}
          </DialogTitle>
          <DialogDescription>
            {isExisting
              ? 'Provide the initial YAML prompt content for this prompt.'
              : 'Define a new prompt key with its initial template content.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Taxonomy selector */}
          <div>
            <Label htmlFor="taxonomy-select">Taxonomy <span className="text-red-500">*</span></Label>
            <Select value={selectedTaxonomyKey} onValueChange={setSelectedTaxonomyKey}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select a taxonomy" />
              </SelectTrigger>
              <SelectContent>
                {taxonomyKeys?.map((t) => (
                  <SelectItem key={t.taxonomyKey} value={t.taxonomyKey}>
                    {t.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1">Links this prompt to a taxonomy for classification.</p>
          </div>

          {!isExisting && (
            <>
              <div>
                <Label htmlFor="prompt-key">Prompt Key <span className="text-red-500">*</span></Label>
                <Input
                  id="prompt-key"
                  placeholder="e.g. sentiment_analysis"
                  value={promptKey}
                  onChange={(e) => setPromptKey(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '_'))}
                  className="mt-1 font-mono"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase with underscores. Must match a dimension ID in the selected taxonomy.
                </p>
                {effectiveKey && !isNonDimension && dimensionIds.length > 0 && !dimensionValid && (
                  <p className="text-xs text-red-600 mt-1">
                    &quot;{effectiveKey}&quot; is not a dimension in this taxonomy.
                    Available dimensions: {dimensionIds.join(', ')}
                  </p>
                )}
                {promptAlreadyExists && (
                  <p className="text-xs text-red-600 mt-1">
                    A prompt for &quot;{effectiveKey}&quot; already exists in this taxonomy.
                    Create a new version of that prompt instead.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="display-name">Display Name</Label>
                <Input
                  id="display-name"
                  placeholder="e.g. Sentiment Analysis Classifier"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Human-readable name. Auto-generated from key if empty.</p>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="template-content">Template Content <span className="text-red-500">*</span></Label>
            <Textarea
              id="template-content"
              placeholder="Write your YAML prompt here..."
              value={templateContent}
              onChange={(e) => setTemplateContent(e.target.value)}
              className="mt-1 font-mono text-sm"
              rows={10}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={createDraft.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!effectiveKey.trim() || !templateContent.trim() || !selectedTaxonomyKey || !dimensionValid || promptAlreadyExists || createDraft.isPending}
          >
            {createDraft.isPending ? 'Creating...' : 'Create Draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreatePromptModal;
